import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export interface DeviceVlanItem {
  id: string;
  vlanId: number;
  vlanName: string | null;
  vlanType: string | null;
  status: string;
  portCount: number;
  accessPortCount: number;
  trunkTaggedPortCount: number;
  trunkNativePortCount: number;
}

export interface DeviceInterfaceVlanItem {
  interfaceId: string;
  interfaceName: string;
  interfaceType: string | null;
  adminStatus: string | null;
  operStatus: string | null;
  /**
   * The interface's VLAN mode — 'access' | 'trunk' | 'hybrid' — or null when it is not yet
   * known, which is the case until a gateway new enough to derive it has polled the device.
   * Repeated on every membership row for the interface; it describes the port, not the row.
   */
  interfacePortMode: string | null;
  vlanId: number;
  vlanName: string | null;
  isNative: boolean;
  isTagged: boolean;
}

export interface DeviceVlansResponse {
  deviceId: string;
  vlans: DeviceVlanItem[];
  interfaceVlans: DeviceInterfaceVlanItem[];
}

export function useDeviceVlans(deviceId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<DeviceVlansResponse>({
    queryKey: ['deviceVlans', deviceId],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID is required');
      const response = await axios.get<DeviceVlansResponse>(
        `${config.backendApi}/api/devices/${deviceId}/vlans`,
        { withCredentials: true }
      );
      return response.data;
    },
    enabled: !!deviceId && contextReady,
  });
}
